/**
 * Forgot Password screen — /parent/forgot-password
 *
 * Sends a Firebase password-reset email so the parent can set a new password.
 * Accessible from the parent login page without authentication.
 */

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { motion } from "motion/react";
import { ChevronLeft } from "lucide-react";
import { auth } from "../lib/firebase";

const BRAND_NAVY = "#161b3d";
const LANG_STORAGE_KEY = "forumLang";

function useLang(): "es" | "en" {
  try {
    const s = localStorage.getItem(LANG_STORAGE_KEY);
    return s === "en" ? "en" : "es";
  } catch (_) {
    return "es";
  }
}

type Stage = "form" | "sent";

export function ForgotPassword() {
  const navigate = useNavigate();
  const lang = useLang();

  const [email, setEmail] = useState("");
  const [stage, setStage] = useState<Stage>("form");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const T = {
    back: lang === "es" ? "Volver" : "Back",
    title: lang === "es" ? "Restablecer contraseña" : "Reset password",
    subtitle:
      lang === "es"
        ? "Ingresa tu correo y te enviaremos un enlace para crear una nueva contraseña."
        : "Enter your email and we'll send you a link to create a new password.",
    emailLabel: lang === "es" ? "Correo electrónico" : "Email address",
    emailPlaceholder:
      lang === "es" ? "correo@ejemplo.com" : "email@example.com",
    emptyError:
      lang === "es"
        ? "Ingresa tu correo electrónico."
        : "Enter your email address.",
    sendError:
      lang === "es"
        ? "No se pudo enviar el correo. Intenta de nuevo."
        : "Could not send the email. Please try again.",
    sending: lang === "es" ? "Enviando…" : "Sending…",
    sendLink: lang === "es" ? "Enviar enlace" : "Send link",
    successTitle: lang === "es" ? "Revisa tu correo" : "Check your email",
    successBody1: lang === "es" ? "Si existe una cuenta con" : "If an account with",
    successBody2:
      lang === "es"
        ? ", recibirás un enlace para restablecer tu contraseña en los próximos minutos."
        : " exists, you'll receive a reset link within the next few minutes.",
    successSpam:
      lang === "es"
        ? "Revisa también la carpeta de spam si no lo ves en tu bandeja de entrada."
        : "Check your spam folder if you don't see it in your inbox.",
    backToLogin:
      lang === "es" ? "Volver al inicio de sesión" : "Back to sign in",
    footer:
      lang === "es"
        ? "American School · Escuela de Talentos"
        : "American School · School of Talents",
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(T.emptyError);
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth!, trimmed);
      setStage("sent");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      if (code === "auth/user-not-found" || code === "auth/invalid-email") {
        // Don't reveal whether the account exists — show the same success screen.
        // This prevents email enumeration attacks.
        setStage("sent");
      } else {
        setError(T.sendError);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="relative min-h-screen flex items-center justify-center bg-[#f5f5f7] p-4 font-sans"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Back button — outside the card column so centering is unaffected */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="absolute top-4 left-4 flex items-center gap-1 text-[13px] font-medium text-[#6e6e73] hover:text-[#161b3d] transition-colors"
      >
        <ChevronLeft size={16} />
        {T.back}
      </button>

      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden"
            style={{ backgroundColor: BRAND_NAVY }}
          >
            <img
              src="/logo/logo.png"
              alt="American School"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-black/[0.06] px-8 py-8">
          {stage === "form" ? (
            <>
              <h1
                className="text-[22px] font-bold tracking-tight text-center mb-1"
                style={{ color: BRAND_NAVY }}
              >
                {T.title}
              </h1>
              <p className="text-[13px] text-[#6e6e73] text-center mb-6">
                {T.subtitle}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[12px] font-semibold text-[#1d1d1f] uppercase tracking-wide mb-1.5">
                    {T.emailLabel}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={T.emailPlaceholder}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#d2d2d7]/80 focus:border-[#161b3d] focus:ring-2 focus:ring-[#161b3d]/20 outline-none text-[14px] text-[#1d1d1f] bg-[#f5f5f7] focus:bg-white transition-all"
                  />
                </div>

                {error && (
                  <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-[13px] text-red-600">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl text-[14px] font-semibold text-white transition-opacity disabled:opacity-60"
                  style={{ backgroundColor: BRAND_NAVY }}
                >
                  {loading ? T.sending : T.sendLink}
                </button>
              </form>
            </>
          ) : (
            <>
              {/* Success state */}
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-green-50 border border-green-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <h1
                className="text-[20px] font-bold tracking-tight text-center mb-1"
                style={{ color: BRAND_NAVY }}
              >
                {T.successTitle}
              </h1>
              <p className="text-[13px] text-[#6e6e73] text-center mb-6">
                {T.successBody1}{" "}
                <span className="font-semibold text-[#1d1d1f]">{email}</span>
                {T.successBody2}
              </p>
              <p className="text-[12px] text-[#86868b] text-center mb-6">
                {T.successSpam}
              </p>
              <Link
                to="/parent/login"
                replace
                className="block w-full text-center py-3 rounded-xl text-[14px] font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: BRAND_NAVY }}
              >
                {T.backToLogin}
              </Link>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-[#86868b]">
          {T.footer}
        </p>
      </div>
    </motion.div>
  );
}
